/**
 * useAIPMOnboarding Composable
 *
 * Task Group 5.2: Singleton composable for AIPM onboarding flow
 *
 * Follows the Auth Orchestrator singleton pattern to ensure all components
 * share the same onboarding instance for proper state synchronization.
 *
 * Features:
 * - Phase-based navigation through onboarding flow
 * - Persona selection management
 * - Phone verification with US formatting
 * - Channel preference handling
 * - First goal collection
 * - GraphQL integration with Nexus API
 */

import { reactive, computed, readonly } from 'vue';
import { useNexusClient } from '@/composables/useApolloClients';
import { getAutoPilotByProgramId } from '@/api/outreachApi';
import gql from 'graphql-tag';
import { useCoaching, tierFor } from '@/composables/useCoaching';
import { useCoachingModule } from '@/composables/useCoachingModule';
import { usePlaces } from '@/composables/usePlaces';
import { useTeamModule } from '@/composables/useTeamModule';
import { isFeatureEnabled } from '@/constants/featureFlags';
import { buildBasePhases } from '@/composables/onboardingPhaseBuilder';
import type {
  ProgramCoachingPolicy,
  PersonaScheduling,
  CoachingModuleCoach,
  CoachingCoveredProgram
} from '@/services/graphql/nexus/coaching';
// ============================================================
// Types
// ============================================================

/**
 * Onboarding phase type
 *
 * Represents each step in the AIPM onboarding flow
 * Note: 'mentor' phase combines the old 'welcome' and 'persona' phases
 * 'contact' phase handles both phone and email verification
 */
export type OnboardingPhase =
  | 'welcome'
  | 'mentor'
  | 'contact'
  | 'channel'
  | 'places'
  | 'coaching'
  | 'team'
  | 'goal'
  | 'confirmation';

/**
 * Preferred communication channel
 */
export type PreferredChannel = 'sms' | 'email';

/**
 * Entry point for onboarding
 */
export type OnboardingEntryPoint =
  | 'checkout'
  | 'retroactive'
  | 'unsponsored'
  | 'participant_initiated';

/**
 * Persona type
 */
export interface Persona {
  id: string;
  name: string;
  profile: string;
  photoUrl?: string;
  greeting?: string;
  signatureStyle?: string;
  type: 'alumni' | 'team' | 'sponsor' | 'creator';
  userId?: string;
}

/**
 * Coordinator settings for onboarding behavior
 */
export interface CoordinatorSettings {
  skippable: boolean;
  defaultPersonaId?: string; // deprecated: use defaultPersonaIds
  defaultPersonaIds?: string[]; // Selected persona IDs to show (empty = all)
  customWelcome?: string;
  phoneRequired?: boolean;
}

/**
 * Phone validation result
 */
export interface PhoneValidationResult {
  isValid: boolean;
  formatted?: string;
  error?: string;
}

/**
 * Email validation result
 */
export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Dismiss result
 */
export interface DismissResult {
  success: boolean;
  reason?: 'onboarding_required' | 'dismissed';
  expiresAt?: Date;
}

/**
 * Message preview for confirmation phase
 */
export interface MessagePreview {
  personaName: string;
  goal?: string;
  channel: PreferredChannel;
  preview?: string;
}

/**
 * Coaching summary (shown in confirmation when coaching was part of onboarding)
 */
export interface CoachingSummary {
  /** Whether the participant booked a call (connected tier) during onboarding. */
  booked: boolean;
  /** Next scheduled call time (connected tier), if known. */
  nextAt: string | null;
  /** The coach (selected persona) name. */
  coach: string;
  /** Resolved booking tier for the picked persona. */
  tier: 'connected' | 'link' | 'none';
}

/**
 * Coach-relationship coverage (loaded from `coachingModuleState`).
 *
 * Coaching satisfaction is COACH-scoped: one call with a coach covers every program
 * that coach runs for the participant. This captures the disclosure surface — the
 * coach, their next call, the participant's programs this coach covers, and the
 * enriched booking URL — so the coaching phase can render an affirmative "already
 * covered" state instead of silently skipping when program B is auto-satisfied by a
 * call booked under program A.
 */
export interface CoachingCoverage {
  /** Whether the participant has met/booked with this coach (sticky). */
  satisfied: boolean;
  /** Next UPCOMING call with this coach (ISO), or null (past/attested). */
  nextCall: string | null;
  /** The coach (persona) this coaching is scoped to. */
  coach: CoachingModuleCoach | null;
  /** The participant's active programs this ONE coach covers. */
  programsCovered: CoachingCoveredProgram[];
  /** Enriched, prefilled Calendly booking URL (prefer over raw scheduling URL). */
  bookingUrl: string | null;
}

/**
 * Confirmation summary
 */
export interface ConfirmationSummary {
  persona: string;
  channel: PreferredChannel;
  goal?: string;
  coaching?: CoachingSummary | null;
}

/**
 * Onboarding state interface
 */
export interface OnboardingState {
  // Dialog state
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;

  // Phase navigation
  currentPhase: OnboardingPhase;

  // Onboarding data
  programId: string | null;
  entryPoint: OnboardingEntryPoint | null;
  sessionId: string | null;

  // Persona selection
  personasLoading: boolean;
  availablePersonas: Persona[];
  selectedPersona: Persona | null;

  // Phone verification
  phoneNumber: string;
  phoneVerified: boolean;
  verificationCode: string;
  verificationSent: boolean;
  verificationAttempts: number;
  resendCountdown: number;

  // Email verification
  email: string;
  emailVerified: boolean;
  emailVerificationSent: boolean;
  emailVerificationCode: string;
  emailResendCountdown: number;

  // Channel preference
  preferredChannel: PreferredChannel;
  availableChannels: PreferredChannel[];

  // Coaching (Stories F-S6/7)
  coachingPolicy: ProgramCoachingPolicy | null;
  personaScheduling: PersonaScheduling | null;
  coachingBooked: boolean;
  coachingSelfAttested: boolean;
  nextCoachingAt: string | null;
  /**
   * The participant ALREADY has a booked coaching session (upcoming nextCall, or a
   * satisfied cadence). Loaded from `coachingModuleState` at onboarding start; when true
   * the coaching phase is SKIPPED (founder decision — don't re-prompt to schedule what's
   * already scheduled). The mid-program cadence nudge resumes after the booked call.
   */
  coachingAlreadyBooked: boolean;

  /**
   * Coach-relationship coverage loaded from `coachingModuleState` (additive). Non-null
   * once the coaching booked-state has been fetched. Drives the coaching phase's
   * "already covered" confirm card AND the silent-skip fix: when a REQUIRED program is
   * auto-satisfied via coach coverage (programsCovered.length > 1 OR an upcoming
   * nextCall), the phase still renders (in the confirm state) so the participant SEES
   * the coverage rather than being silently skipped.
   */
  coachingCoverage: CoachingCoverage | null;

  /**
   * The program has an ENABLED Places module (PW5). Loaded from placesModuleState at
   * onboarding start, behind PROGRAM_MODULES + PLACES_V1 (else stays false → the Places
   * pledge phase never appears → byte-identical). Read by the places descriptor's
   * onboardingPhase.shouldShow via the phase-list builder.
   */
  placesEnabled: boolean;

  /**
   * Team Onboarding (soft/optional phase). Loaded from teamModuleState at onboarding start,
   * behind PROGRAM_MODULES + TEAM_ONBOARDING (else all stay false/'at-activity' → the team
   * phase never appears → byte-identical). The team phase shows only when teamEnabled &&
   * teamPromptTiming === 'onboarding' && !teamOnTeam (see shouldShowTeam).
   *   - teamEnabled       — the team module is on for this program (gate.enabled !== false)
   *   - teamOnTeam        — the participant is already on a team (skip the invite)
   *   - teamPromptTiming  — the creator's opt-in: 'onboarding' surfaces the phase
   */
  teamEnabled: boolean;
  teamOnTeam: boolean;
  teamPromptTiming: 'at-activity' | 'onboarding';

  // Goal
  firstGoal: string;

  // Completion state
  isComplete: boolean;
  completedAt: Date | null;
  messagePreview: MessagePreview | null;
  confirmationSummary: ConfirmationSummary | null;

  // Coordinator settings
  coordinatorSettings: CoordinatorSettings;

  // Dismissal tracking
  lastDismissedAt: Date | null;

  // Check-first verification flags (skip if already verified)
  skipPhoneVerification: boolean;
  skipEmailVerification: boolean;
}

// ============================================================
// Phase Order
// ============================================================

// BASE_PHASES + the module-contributed phase-list builder now live in
// `onboardingPhaseBuilder.ts` (extracted for isolated unit-testing). Imported above.
// Under PROGRAM_MODULES the phase list is built by concatenating module-contributed
// phases into BASE_PHASES at coaching's position; empty contribution this wave =
// BASE_PHASES byte-for-byte (asserted in useProgramModulesFramework.spec.ts).

// ============================================================
// GraphQL Operations
// ============================================================

const INITIALIZE_ONBOARDING = gql`
  mutation InitializeAIPMOnboarding($input: InitializeOnboardingInput!) {
    initializeAIPMOnboarding(input: $input) {
      sessionId
      entryPoint
      userId
    }
  }
`;

const SELECT_PERSONA = gql`
  mutation SelectAIPMPersona($input: SelectPersonaInput!) {
    selectAIPMPersona(input: $input) {
      personaId
    }
  }
`;

const SEND_PHONE_VERIFICATION = gql`
  mutation SendAIPMPhoneVerification($input: SendPhoneVerificationInput!) {
    sendAIPMPhoneVerification(input: $input) {
      sent
      attemptsRemaining
    }
  }
`;

const VERIFY_PHONE = gql`
  mutation VerifyAIPMPhone($input: VerifyPhoneInput!) {
    verifyAIPMPhone(input: $input) {
      verified
      error
    }
  }
`;

const SEND_EMAIL_VERIFICATION = gql`
  mutation SendAIPMEmailVerification($input: SendEmailVerificationInput!) {
    sendAIPMEmailVerification(input: $input) {
      sent
      attemptsRemaining
    }
  }
`;

const VERIFY_EMAIL = gql`
  mutation VerifyAIPMEmail($input: VerifyEmailInput!) {
    verifyAIPMEmail(input: $input) {
      verified
      error
    }
  }
`;

const COMPLETE_ONBOARDING = gql`
  mutation CompleteAIPMOnboarding($input: CompleteOnboardingInput!) {
    completeAIPMOnboarding(input: $input) {
      completed
      messageId
    }
  }
`;

const DISMISS_ONBOARDING = gql`
  mutation DismissAIPMOnboarding($input: DismissOnboardingInput!) {
    dismissAIPMOnboarding(input: $input) {
      dismissed
      expiresAt
    }
  }
`;

const GET_ACTIVE_PERSONAS = gql`
  query ActivePersonas {
    activePersonas {
      id
      name
      profile
      photoUrl
      greeting
      type
    }
  }
`;

const GET_ONBOARDING_STATUS = gql`
  query AIPMOnboardingStatus($input: OnboardingStatusInput!) {
    aipmOnboardingStatus(input: $input) {
      funnelState
      steps {
        welcome
        persona
        phone
        channel
        goal
        confirmation
      }
      currentStep
      completedAt
    }
  }
`;

const GET_VERIFICATION_STATUS = gql`
  query GetVerificationStatus {
    getVerificationStatus {
      phone
      phoneVerified
      email
      emailVerified
      skipPhoneVerification
      skipEmailVerification
    }
  }
`;

// ============================================================
// Singleton Instance
// ============================================================

// eslint-disable-next-line no-use-before-define -- createOnboardingComposable is defined below, this is just a type reference
let onboardingInstance: ReturnType<typeof createOnboardingComposable> | null = null;
let cachedNexusClient: ReturnType<typeof useNexusClient> | null = null;
let cachedCoaching: ReturnType<typeof useCoaching> | null = null;
let cachedCoachingModule: ReturnType<typeof useCoachingModule> | null = null;
let cachedPlaces: ReturnType<typeof usePlaces> | null = null;
let cachedTeam: ReturnType<typeof useTeamModule> | null = null;

/**
 * Get or cache the Nexus Apollo client
 * Must be called within Vue component setup context at least once
 */
function getNexusClient() {
  if (!cachedNexusClient) {
    try {
      cachedNexusClient = useNexusClient();
    } catch (e) {
      console.error('[useAIPMOnboarding] Failed to get Nexus client:', e);
      throw new Error('Apollo client not available. Ensure this is called within a Vue component.');
    }
  }
  return cachedNexusClient;
}

/**
 * Get or cache the coaching composable (Stories F-S6/7).
 *
 * useCoaching() resolves the Nexus Apollo client at construction, so — like
 * getNexusClient() — it must first be reached within a Vue component setup
 * context. We cache it on first successful construction.
 */
function getCoaching() {
  if (!cachedCoaching) {
    try {
      // Pass the synchronously-cached Nexus client — getCoaching() is reached async (after
      // awaits in startOnboarding), where useCoaching()'s own useNexusClient() would throw.
      cachedCoaching = useCoaching(getNexusClient());
    } catch (e) {
      console.error('[useAIPMOnboarding] Failed to get coaching composable:', e);
      throw new Error('Apollo client not available. Ensure this is called within a Vue component.');
    }
  }
  return cachedCoaching;
}

/**
 * Get or cache the coaching MODULE composable (Program-Modules epic, Wave 4).
 *
 * Distinct from getCoaching(): this wraps the framework's `coachingModuleState` op
 * (the SAME state the mid-program cadence nudge reads), which the onboarding uses to
 * SKIP the "book your first 1:1" phase when a session is already booked. Lazy for the
 * same Apollo-at-construction reason as getCoaching(); silent-degrades internally.
 */
function getCoachingModule() {
  if (!cachedCoachingModule) {
    try {
      // Pass the synchronously-cached Nexus client (async-context safe; see getCoaching).
      cachedCoachingModule = useCoachingModule(getNexusClient());
    } catch (e) {
      console.error('[useAIPMOnboarding] Failed to get coaching module composable:', e);
      throw new Error('Apollo client not available. Ensure this is called within a Vue component.');
    }
  }
  return cachedCoachingModule;
}

/**
 * Get or cache the Places composable (PW5). Lazy for the same Apollo-at-construction
 * reason as getCoaching(); silent-degrades internally. Only reached behind the flags.
 */
function getPlaces() {
  if (!cachedPlaces) {
    try {
      // Pass the synchronously-cached Nexus client — getPlaces() is reached from an async
      // context (after awaits in startOnboarding), where usePlaces()'s own useNexusClient()
      // would throw "Apollo client with id nexus not found". getNexusClient() returns the
      // client warmed during setup, so this resolves cleanly.
      cachedPlaces = usePlaces(getNexusClient());
    } catch (e) {
      console.error('[useAIPMOnboarding] Failed to get places composable:', e);
      throw new Error('Apollo client not available. Ensure this is called within a Vue component.');
    }
  }
  return cachedPlaces;
}

/**
 * Get or cache the Team module composable (Team Onboarding). Lazy for the same
 * Apollo-at-construction reason as getPlaces(); silent-degrades internally. Only reached
 * behind PROGRAM_MODULES + TEAM_ONBOARDING. useTeamModule now accepts an already-resolved
 * client (like usePlaces / useCoachingModule), so we pass the synchronously-warmed client —
 * getTeam() is reached from an async context (after awaits in startOnboarding), where a fresh
 * useNexusClient() would throw "Apollo client with id nexus not found".
 */
function getTeam() {
  if (!cachedTeam) {
    try {
      cachedTeam = useTeamModule(getNexusClient());
    } catch (e) {
      console.error('[useAIPMOnboarding] Failed to get team module composable:', e);
      throw new Error('Apollo client not available. Ensure this is called within a Vue component.');
    }
  }
  return cachedTeam;
}

// ============================================================
// Permanent Dismissal Helpers (localStorage per-program)
// ============================================================

/**
 * Check if onboarding was permanently dismissed for a program
 */
export function isPermanentlyDismissed(programId: string): boolean {
  try {
    return localStorage.getItem(`aipm_onboarding_dismissed_${programId}`) === 'true';
  } catch {
    return false;
  }
}

/**
 * Record permanent dismissal for a program
 */
export function setPermanentlyDismissed(programId: string): void {
  try {
    localStorage.setItem(`aipm_onboarding_dismissed_${programId}`, 'true');
  } catch {
    // localStorage not available
  }
}

/**
 * Clear permanent dismissal for a program (for manual re-access)
 */
export function clearPermanentDismissal(programId: string): void {
  try {
    localStorage.removeItem(`aipm_onboarding_dismissed_${programId}`);
  } catch {
    // localStorage not available
  }
}

// ============================================================
// Centralized Gate
// ============================================================

/**
 * Centralized gate that ALL trigger paths must go through.
 * Checks global flag, AutoPilot status, onboardingConfig, completion, and permanent dismissal.
 *
 * @param programId - The program ID
 * @param autoPilot - The AutoPilot document for this program
 * @param onboardingState - Optional onboarding state to check completion
 */
export function canStartOnboarding(
  programId: string,
  autoPilot: any,
  onboardingState?: { isComplete?: boolean }
): boolean {
  // NOTE: Global kill switch (FEATURE_FLAGS.AIPM_ONBOARDING_KILL_SWITCH) is intentionally
  // NOT checked here. It is only used for the retroactive trigger path in App.vue.
  // Checkout-based triggers are gated by per-program AutoPilot + onboardingConfig.

  // 1. AutoPilot must be active
  if (!autoPilot || autoPilot.status !== 'active') return false;

  // 2. Per-program onboardingConfig must exist
  if (!autoPilot.onboardingConfig) return false;

  // 3. Master toggle must be enabled (backwards compat: missing field treated as enabled)
  if (autoPilot.onboardingConfig.enabled === false) return false;

  // 4. Already completed onboarding
  if (onboardingState?.isComplete) return false;

  // 5. Permanently dismissed — skip for required onboarding (can't be dismissed via UI,
  // and if an admin switches from skippable→required, stale localStorage shouldn't block)
  if (!autoPilot.onboardingConfig.required && isPermanentlyDismissed(programId)) return false;

  return true;
}

/**
 * Create the onboarding composable
 *
 * Internal factory function that creates the actual composable logic.
 * Called once to create the singleton instance.
 */
// eslint-disable-next-line @typescript-eslint/no-use-before-define
function createOnboardingComposable() {
  /* eslint-disable no-use-before-define */
  // Disable no-use-before-define for this entire function scope.
  // All helper functions are defined within this closure and will be available
  // at runtime before any of them are called.

  // Create reactive state
  const state = reactive<OnboardingState>({
    // Dialog state
    isOpen: false,
    isLoading: false,
    error: null,

    // Phase navigation
    currentPhase: 'welcome',

    // Onboarding data
    programId: null,
    entryPoint: null,
    sessionId: null,

    // Persona selection
    personasLoading: false,
    availablePersonas: [],
    selectedPersona: null,

    // Phone verification
    phoneNumber: '',
    phoneVerified: false,
    verificationCode: '',
    verificationSent: false,
    verificationAttempts: 0,
    resendCountdown: 0,

    // Email verification
    email: '',
    emailVerified: false,
    emailVerificationSent: false,
    emailVerificationCode: '',
    emailResendCountdown: 0,

    // Channel preference
    preferredChannel: 'sms',
    availableChannels: ['sms', 'email'] as Array<'sms' | 'email'>,

    // Coaching (Stories F-S6/7)
    coachingPolicy: null,
    personaScheduling: null,
    coachingBooked: false,
    coachingSelfAttested: false,
    nextCoachingAt: null,
    coachingAlreadyBooked: false,
    coachingCoverage: null,

    // Places (PW5) — off unless a places-enabled program loads it behind the flags.
    placesEnabled: false,

    // Team Onboarding — off unless a team-enabled program opts in behind the flags.
    teamEnabled: false,
    teamOnTeam: false,
    teamPromptTiming: 'at-activity',

    // Goal
    firstGoal: '',

    // Completion state
    isComplete: false,
    completedAt: null,
    messagePreview: null,
    confirmationSummary: null,

    // Coordinator settings
    coordinatorSettings: {
      skippable: true,
      phoneRequired: false
    },

    // Dismissal tracking
    lastDismissedAt: null,

    // Check-first verification flags
    skipPhoneVerification: false,
    skipEmailVerification: false
  });

  // Countdown timer for phone resend
  let resendTimer: ReturnType<typeof setInterval> | null = null;
  // Countdown timer for email resend
  let emailResendTimer: ReturnType<typeof setInterval> | null = null;

  // ============================================================
  // Computed Properties
  // ============================================================

  // Phase order — welcome phase first for all entry points
  // Skip channel selection phase when only one channel is available (auto-selected)
  // Show the coaching phase only when the feature is on, the program enables
  // coaching, the picked persona (coach) has scheduling, and that scheduling
  // resolves to a bookable tier. No fallback: a coach without scheduling skips.
  // Coach-relationship COVERAGE worth disclosing: the coaching is already satisfied AND
  // there's something to tell the participant — either this one coach covers more than one
  // of their programs (programsCovered.length > 1) OR there's an upcoming scheduled call.
  // This is the signal that flips the old SILENT skip into an affirmative "already covered"
  // confirm state. A plainly-satisfied single-program case (nothing to disclose) is NOT
  // disclosable → the existing skip stands.
  const coachingCoverageDisclosable = computed<boolean>(() => {
    const cov = state.coachingCoverage;
    if (!cov?.satisfied) return false;
    const multiProgram = (cov.programsCovered?.length ?? 0) > 1;
    const hasNextCall = !!cov.nextCall;
    return multiProgram || hasNextCall;
  });

  const shouldShowCoaching = computed<boolean>(
    () =>
      isFeatureEnabled('ENABLE_COACHING') &&
      !!state.coachingPolicy?.enabled &&
      !!state.personaScheduling &&
      tierFor(state.personaScheduling) !== 'none' &&
      // Normally SKIP when a session is already booked — don't re-prompt to schedule what's
      // already scheduled; the mid-program cadence nudge takes over after the booked call.
      // EXCEPTION (silent-skip fix): when the satisfaction comes from COACH-relationship
      // coverage on a REQUIRED program (this call covers ANOTHER of the participant's
      // programs, or there's an upcoming call), keep rendering — in the non-blocking
      // "already covered" confirm state — so the participant SEES that their call covers
      // this program too, instead of the phase vanishing with no explanation.
      (!state.coachingAlreadyBooked ||
        (!!state.coachingPolicy?.required && coachingCoverageDisclosable.value))
  );

  // Show the team phase (soft/optional, hardcoded+filter — like coaching) only when the
  // TEAM_ONBOARDING flag is on, the program's team module is enabled, the creator opted the
  // invite into onboarding (promptTiming === 'onboarding'), and the participant isn't already
  // on a team. Flag/gate off → false → the 'team' phase is filtered out of phaseOrder →
  // byte-identical to today.
  const shouldShowTeam = computed<boolean>(
    () =>
      isFeatureEnabled('TEAM_ONBOARDING') &&
      state.teamEnabled &&
      state.teamPromptTiming === 'onboarding' &&
      !state.teamOnTeam
  );

  const phaseOrder = computed<OnboardingPhase[]>(() => {
    // Under PROGRAM_MODULES the base list is built from module contributions (empty
    // this wave → identical to BASE_PHASES); flag-off uses the hardcoded array.
    let phases = ['welcome' as OnboardingPhase, ...buildBasePhases(state)];
    if (state.availableChannels.length <= 1) {
      phases = phases.filter(p => p !== 'channel');
    }
    if (!shouldShowCoaching.value) {
      phases = phases.filter(p => p !== 'coaching');
    }
    if (!shouldShowTeam.value) {
      phases = phases.filter(p => p !== 'team');
    }
    return phases;
  });

  const currentPhaseIndex = computed(() => phaseOrder.value.indexOf(state.currentPhase));

  const progress = computed(() => {
    const index = currentPhaseIndex.value;
    return Math.round(((index + 1) / phaseOrder.value.length) * 100);
  });

  const canGoBack = computed(() => currentPhaseIndex.value > 0);

  const canGoNext = computed(() => {
    // Validate current phase before allowing next
    switch (state.currentPhase) {
      case 'welcome':
        return true; // Welcome is informational, always allows next
      case 'mentor':
        return state.selectedPersona !== null;
      case 'contact':
        // Contact phase can proceed if at least one method is verified
        return state.phoneVerified || state.emailVerified;
      case 'channel':
        return state.preferredChannel !== null;
      case 'places':
        // The Places pledge phase (PW5) gates itself: its Continue button builds the
        // pledge, calls setPlacesPledge, and only emits `continue` once the pledge is
        // saved (auto-approved or warn-and-allow), so navigation is always permitted here.
        return true;
      case 'team':
        // The Team invite phase is SOFT/OPTIONAL (never blocks): the participant can always
        // advance whether or not they joined/created a team. Mirrors the 'places' case —
        // navigation is always permitted; joining is encouraged, not required, here.
        return true;
      case 'coaching':
        // Optional coaching: always proceed when not required, or when the
        // policy explicitly allows booking later.
        if (!state.coachingPolicy?.required) return true;
        if (state.coachingPolicy?.allowBookLater) return true;
        // Coach-relationship coverage: an existing/upcoming call with this coach already
        // satisfies the requirement (rendered as the non-blocking "already covered" card) —
        // never trap the participant behind a call they already have.
        if (state.coachingCoverage?.satisfied) return true;
        // Required coaching: accept an auto-detected booking (connected widget's
        // event_scheduled) OR a self-attestation fallback (link tier, or when the
        // widget event didn't fire) — so the participant is never trapped.
        return state.coachingBooked || state.coachingSelfAttested;
      case 'goal':
        return true; // Goal is optional
      case 'confirmation':
        return false; // Last phase
      default:
        return false;
    }
  });

  const isDialogOpen = computed(() => state.isOpen);

  // ============================================================
  // Navigation Methods
  // ============================================================

  /**
   * Navigate to the next phase
   */
  function nextPhase(): void {
    const phases = phaseOrder.value;
    const currentIndex = currentPhaseIndex.value;
    // Guard currentIndex >= 0: if the current phase was just filtered OUT of phaseOrder
    // (indexOf → -1), `-1 + 1 = 0` would silently jump to the FIRST phase. Never advance
    // from an unknown position — a dynamically-removed phase must not reset onboarding.
    if (currentIndex >= 0 && currentIndex < phases.length - 1) {
      state.currentPhase = phases[currentIndex + 1];
    }
  }

  /**
   * Navigate to the previous phase
   */
  function prevPhase(): void {
    const phases = phaseOrder.value;
    const currentIndex = currentPhaseIndex.value;
    if (currentIndex > 0) {
      state.currentPhase = phases[currentIndex - 1];
    }
  }

  /**
   * Navigate directly to a specific phase
   */
  function goToPhase(phase: OnboardingPhase): void {
    if (phaseOrder.value.includes(phase)) {
      state.currentPhase = phase;
    }
  }

  // ============================================================
  // Onboarding Flow Methods
  // ============================================================

  /**
   * Hydrate channel + coaching config from an AutoPilot WITHOUT running the full
   * startOnboarding flow. Used by entry paths that open the dialog manually
   * (e.g. App.vue's retroactive flow, which bypasses the initialize mutation):
   * narrows `availableChannels` to the program's enabled channels and loads the
   * coaching policy so the coaching phase can appear. Persona scheduling still
   * loads via the mentor phase's auto-select. Mirrors startOnboarding's config
   * loading (channels ~693-704, coaching policy ~741-752).
   */
  /**
   * Load the participant's coaching booking status from `coachingModuleState` (the SAME
   * state the mid-program cadence nudge reads) and set `coachingAlreadyBooked`.
   *
   * "Already booked" = an upcoming session (`nextCall`) OR a satisfied-and-idle cadence
   * (`state === 'idle' && satisfied` — a past call keeps `satisfied` sticky). When true
   * the coaching onboarding phase is SKIPPED. Silent-degrades to false on any error so a
   * missing/failing backend never blocks onboarding. No-op when coaching is off.
   */
  async function refreshCoachingBookedState(programId: string): Promise<void> {
    if (!isFeatureEnabled('ENABLE_COACHING') || !programId) return;
    try {
      const moduleState = await getCoachingModule().loadCoachingModuleState(programId);
      const hasUpcoming = !!moduleState?.nextCall;
      const satisfiedIdle = moduleState?.state === 'idle' && moduleState?.satisfied === true;
      state.coachingAlreadyBooked = hasUpcoming || satisfiedIdle;
      // Capture the coach-relationship coverage (additive). Powers the "already covered"
      // confirm card AND the silent-skip fix — see shouldShowCoaching.
      state.coachingCoverage = {
        satisfied: moduleState?.satisfied === true,
        nextCall: moduleState?.nextCall ?? null,
        coach: moduleState?.coach ?? null,
        programsCovered: moduleState?.programsCovered ?? [],
        bookingUrl: moduleState?.bookingUrl ?? null
      };
    } catch (err: any) {
      // Never block onboarding on this check — default to showing the phase.
      state.coachingAlreadyBooked = false;
      state.coachingCoverage = null;
      console.warn('[useAIPMOnboarding] coaching booked-state load failed:', err?.message);
    }
  }

  /**
   * Load whether the program has an ENABLED Places module (PW5) and set
   * `state.placesEnabled` — the signal the phase-list builder reads (via the places
   * descriptor's onboardingPhase.shouldShow) to insert the Places pledge phase before
   * coaching. Gated HARD behind PROGRAM_MODULES + PLACES_V1: flag-off returns immediately,
   * leaving placesEnabled false → no Places phase → byte-identical onboarding. Silent-
   * degrades to false on any error so a missing backend never blocks onboarding.
   */
  async function refreshPlacesEnabled(programId: string): Promise<void> {
    if (!isFeatureEnabled('PROGRAM_MODULES') || !isFeatureEnabled('PLACES_V1') || !programId) {
      return;
    }
    try {
      const moduleState = await getPlaces().loadPlacesModuleState(programId);
      state.placesEnabled = !!moduleState?.enabled;
    } catch (err: any) {
      state.placesEnabled = false;
      console.warn('[useAIPMOnboarding] places enabled-state load failed:', err?.message);
    }
  }

  /**
   * Load the program's TEAM module state (Team Onboarding) and set the three team signals the
   * phase gate reads: `teamEnabled`, `teamOnTeam`, `teamPromptTiming`. Gated on the feature's
   * OWN flag, TEAM_ONBOARDING — NOT the broad PROGRAM_MODULES FE master gate. The team module
   * is BE-gated on PROGRAM_MODULES server-side (teamModuleState returns {enabled:false} when
   * it's off), so coupling the FE to the master flag too would wrongly require un-darking the
   * ENTIRE Program-Modules FE epic just to surface this one phase — even when the backend
   * already serves the data. Flag-off (TEAM_ONBOARDING) returns immediately → defaults
   * (false/false/'at-activity') → shouldShowTeam false → no team phase → byte-identical
   * onboarding. Silent-degrades to those same defaults on any error (incl. a BE with
   * PROGRAM_MODULES off → {enabled:false}), so a missing/failing backend never blocks onboarding.
   */
  async function refreshTeamEnabled(programId: string): Promise<void> {
    if (!isFeatureEnabled('TEAM_ONBOARDING') || !programId) {
      return;
    }
    try {
      const ms = await getTeam().loadTeamModuleState(programId);
      state.teamEnabled = !!ms?.enabled && ms?.gate?.enabled !== false;
      state.teamOnTeam = !!ms?.gate?.onTeam;
      state.teamPromptTiming = ms?.gate?.promptTiming ?? 'at-activity';
    } catch (err: any) {
      state.teamEnabled = false;
      state.teamOnTeam = false;
      state.teamPromptTiming = 'at-activity';
      console.warn('[useAIPMOnboarding] team enabled-state load failed:', err?.message);
    }
  }

  async function hydrateConfigFromAutoPilot(programId: string, autoPilot?: any): Promise<void> {
    // Places (PW5) — no-op behind the flags; sets state.placesEnabled for the builder.
    await refreshPlacesEnabled(programId);
    // Team Onboarding — no-op behind the flags; sets the team signals shouldShowTeam reads.
    await refreshTeamEnabled(programId);

    if (autoPilot?.channels) {
      const channels: Array<'sms' | 'email'> = [];
      if (autoPilot.channels.sms?.enabled) channels.push('sms');
      if (autoPilot.channels.email?.enabled) channels.push('email');
      if (channels.length > 0) {
        state.availableChannels = channels;
        if (channels.length === 1) {
          [state.preferredChannel] = channels;
        }
      }
    }
    if (isFeatureEnabled('ENABLE_COACHING')) {
      try {
        const coaching = getCoaching();
        state.coachingPolicy = await coaching.loadProgramCoachingPolicy(programId);
      } catch (coachingError: any) {
        console.warn(
          '[useAIPMOnboarding] hydrateConfigFromAutoPilot: coaching policy load failed:',
          coachingError?.message
        );
        state.coachingPolicy = null;
      }
      // Skip the coaching phase if a session is already booked.
      await refreshCoachingBookedState(programId);
    }
  }

  /**
   * Start the onboarding flow
   *
   * @param programId - The program ID
   * @param entryPoint - How the user entered onboarding
   * @param autoPilot - Optional AutoPilot document for centralized gate check
   */
  async function startOnboarding(
    programId: string,
    entryPoint: OnboardingEntryPoint = 'participant_initiated',
    autoPilot?: any
  ): Promise<void> {
    console.log('[useAIPMOnboarding] startOnboarding called with:', { programId, entryPoint });

    // Safety net: if autoPilot data is provided, run centralized gate
    // participant_initiated bypasses the gate (explicit user action from Settings)
    if (autoPilot && entryPoint !== 'participant_initiated') {
      if (!canStartOnboarding(programId, autoPilot, state)) {
        console.log('[useAIPMOnboarding] Blocked by centralized gate');
        return;
      }
    }

    state.isLoading = true;
    state.personasLoading = true;
    state.error = null;
    state.programId = programId;
    state.entryPoint = entryPoint;

    // Restrict the channel picker to the program's configured channels. Callers like
    // checkout call startOnboarding without an autoPilot, so fetch it when missing —
    // otherwise the channel step defaults to offering BOTH SMS and Email regardless of
    // the program's actual config (e.g. an SMS-only program).
    let channelAutoPilot = autoPilot;
    if (!channelAutoPilot) {
      try {
        channelAutoPilot = await getAutoPilotByProgramId(programId);
      } catch {
        channelAutoPilot = null;
      }
    }
    applyAutoPilotChannels(channelAutoPilot);

    // Open dialog FIRST, then load data in background
    // This ensures users see the onboarding even if API calls fail
    state.isOpen = true;
    state.currentPhase = 'welcome';
    console.log(
      '[useAIPMOnboarding] Dialog opened, currentPhase:',
      state.currentPhase,
      'entryPoint:',
      entryPoint
    );

    try {
      // Initialize session in backend (non-blocking for UI)
      const client = getNexusClient();
      console.log('[useAIPMOnboarding] Calling INITIALIZE_ONBOARDING mutation...');
      const { data } = await client.mutate({
        mutation: INITIALIZE_ONBOARDING,
        variables: {
          input: { programId, entryPoint }
        }
      });
      console.log('[useAIPMOnboarding] INITIALIZE_ONBOARDING response:', data);

      if (data?.initializeAIPMOnboarding) {
        state.sessionId = data.initializeAIPMOnboarding.sessionId;
      }

      // Fetch verification status for check-first logic
      await fetchVerificationStatus();

      // Load available personas
      await loadPersonas();

      // Load the program coaching policy (Stories F-S6/7). Non-fatal: if it
      // fails or coaching is off, the coaching phase simply won't show.
      if (isFeatureEnabled('ENABLE_COACHING')) {
        try {
          const coaching = getCoaching();
          state.coachingPolicy = await coaching.loadProgramCoachingPolicy(programId);
        } catch (coachingError: any) {
          console.warn(
            '[useAIPMOnboarding] Failed to load coaching policy:',
            coachingError?.message
          );
          state.coachingPolicy = null;
        }
        // Skip the coaching phase if a session is already booked (upcoming nextCall or a
        // satisfied cadence). Silent-degrades to showing the phase on failure.
        await refreshCoachingBookedState(programId);
      }

      // Places (PW5) — set placesEnabled for the phase-list builder. No-op behind the
      // flags → the Places pledge phase never appears → byte-identical onboarding.
      await refreshPlacesEnabled(programId);

      // Team Onboarding — set the team signals shouldShowTeam reads. No-op behind the flags →
      // the team phase never appears → byte-identical onboarding.
      await refreshTeamEnabled(programId);
    } catch (error: any) {
      // Don't close dialog on error - user can still proceed with defaults
      state.error = error.message || 'Failed to start onboarding';
      console.error('[useAIPMOnboarding] Start onboarding error:', error);

      // Ensure we have fallback personas if load failed
      if (state.availablePersonas.length === 0) {
        console.log('[useAIPMOnboarding] Using fallback persona');
        state.availablePersonas = [
          {
            id: 'peachy-fallback',
            name: 'Peachy',
            profile:
              "PilotCity's default AI program manager — guides you through your program journey.",
            photoUrl: '',
            greeting:
              "Hi! I'm Peachy, your AI program manager. I'll guide you through your program journey.",
            type: 'team'
          }
        ];
      }
    } finally {
      state.isLoading = false;
    }
  }

  /**
   * Fetch verification status for check-first logic
   *
   * Queries the API to get current phone/email verification status.
   * If already verified, pre-fills the data and sets skip flags.
   */
  async function fetchVerificationStatus(): Promise<void> {
    try {
      const client = getNexusClient();
      const { data } = await client.query({
        query: GET_VERIFICATION_STATUS,
        fetchPolicy: 'network-only'
      });

      if (data?.getVerificationStatus) {
        const status = data.getVerificationStatus;
        console.log('[useAIPMOnboarding] Verification status:', status);

        // Pre-fill phone data if available
        if (status.phone) {
          const validation = validatePhoneNumber(status.phone);
          if (validation.isValid && validation.formatted) {
            state.phoneNumber = validation.formatted;
          } else {
            state.phoneNumber = status.phone;
          }
        }

        // Set phone verification status
        if (status.phoneVerified) {
          state.phoneVerified = true;
        }

        // Set skip flag for phone verification
        state.skipPhoneVerification = status.skipPhoneVerification || false;

        // Pre-fill email data if available
        if (status.email) {
          state.email = status.email.trim().toLowerCase();
        }

        // Set email verification status
        if (status.emailVerified) {
          state.emailVerified = true;
        }

        // Set skip flag for email verification
        state.skipEmailVerification = status.skipEmailVerification || false;

        console.log('[useAIPMOnboarding] Pre-filled from verification status:', {
          phone: state.phoneNumber,
          phoneVerified: state.phoneVerified,
          skipPhoneVerification: state.skipPhoneVerification,
          email: state.email,
          emailVerified: state.emailVerified,
          skipEmailVerification: state.skipEmailVerification
        });
      }
    } catch (error: any) {
      // Don't fail onboarding if verification status fetch fails
      console.warn('[useAIPMOnboarding] Failed to fetch verification status:', error.message);
    }
  }

  /**
   * Load available personas from the API
   */
  async function loadPersonas(): Promise<void> {
    state.personasLoading = true;
    try {
      const client = getNexusClient();
      const { data } = await client.query({
        query: GET_ACTIVE_PERSONAS,
        fetchPolicy: 'network-only'
      });

      if (data?.activePersonas) {
        const allPersonas = data.activePersonas;
        // Filter by selected persona IDs (supports multi-select or legacy single-select)
        let selectedIds: string[] = [];
        if (state.coordinatorSettings.defaultPersonaIds?.length) {
          selectedIds = state.coordinatorSettings.defaultPersonaIds;
        } else if (state.coordinatorSettings.defaultPersonaId) {
          selectedIds = [state.coordinatorSettings.defaultPersonaId];
        }

        if (selectedIds.length > 0) {
          const filtered = allPersonas.filter((p: Persona) => selectedIds.includes(p.id));
          state.availablePersonas = filtered.length > 0 ? filtered : allPersonas;
        } else {
          state.availablePersonas = allPersonas;
        }
      }
    } catch (error: any) {
      console.error('[useAIPMOnboarding] Load personas error:', error);
    } finally {
      state.personasLoading = false;
    }
  }

  /**
   * Select a persona
   */
  async function selectPersona(persona: Persona): Promise<void> {
    state.selectedPersona = persona;

    // The selected persona IS the coach. Load their scheduling so the coaching
    // phase can resolve its booking tier (or be skipped if there's none).
    // Reset any prior booking/attestation state for the freshly-picked coach.
    state.coachingBooked = false;
    state.coachingSelfAttested = false;
    state.nextCoachingAt = null;
    if (persona.id && isFeatureEnabled('ENABLE_COACHING')) {
      try {
        const coaching = getCoaching();
        state.personaScheduling = await coaching.loadPersonaScheduling(persona.id);
      } catch (error: any) {
        console.error('[useAIPMOnboarding] Load persona scheduling error:', error);
        state.personaScheduling = null;
      }
    } else {
      state.personaScheduling = null;
    }

    if (state.programId && persona.id) {
      try {
        const client = getNexusClient();
        await client.mutate({
          mutation: SELECT_PERSONA,
          variables: {
            input: { personaId: persona.id }
          }
        });
      } catch (error: any) {
        console.error('[useAIPMOnboarding] Select persona error:', error);
        // Keep local state even if API fails
      }
    }
  }

  // ============================================================
  // Phone Verification Methods
  // ============================================================

  /**
   * Validate a phone number (US format)
   */
  function validatePhoneNumber(phone: string): PhoneValidationResult {
    if (!phone || phone.trim() === '') {
      return { isValid: false, error: 'Phone number is required' };
    }

    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');

    // Check for valid US phone number (10 digits, or 11 with leading 1)
    if (digitsOnly.length === 10) {
      return {
        isValid: true,
        formatted: `+1${digitsOnly}`
      };
    }
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return {
        isValid: true,
        formatted: `+${digitsOnly}`
      };
    }

    return {
      isValid: false,
      error: 'Please enter a valid 10-digit US phone number'
    };
  }

  /**
   * Format phone number to E.164 format
   */
  function formatPhoneNumber(phone: string): string {
    const digitsOnly = phone.replace(/\D/g, '');

    if (digitsOnly.length === 10) {
      return `+1${digitsOnly}`;
    }
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return `+${digitsOnly}`;
    }

    return phone;
  }

  /**
   * Set phone number with validation and formatting
   * Resets verification status when phone is changed/cleared
   */
  async function setPhoneNumber(phone: string): Promise<void> {
    // If phone is being cleared or changed, reset verification status
    if (!phone || phone !== state.phoneNumber) {
      state.phoneVerified = false;
      state.verificationSent = false;
    }

    const validation = validatePhoneNumber(phone);
    if (validation.isValid && validation.formatted) {
      state.phoneNumber = validation.formatted;
    } else {
      state.phoneNumber = phone;
    }
  }

  /**
   * Send phone verification OTP
   */
  async function sendVerification(): Promise<boolean> {
    if (!state.phoneNumber) {
      state.error = 'Please enter a phone number';
      return false;
    }

    state.isLoading = true;
    state.error = null;

    try {
      const client = getNexusClient();
      const { data } = await client.mutate({
        mutation: SEND_PHONE_VERIFICATION,
        variables: {
          input: { phoneNumber: state.phoneNumber }
        }
      });

      if (data?.sendAIPMPhoneVerification?.sent) {
        state.verificationSent = true;
        state.verificationAttempts = data.sendAIPMPhoneVerification.attemptsRemaining;
        startResendCountdown();
        return true;
      }

      return false;
    } catch (error: any) {
      state.error = error.message || 'Failed to send verification code';
      return false;
    } finally {
      state.isLoading = false;
    }
  }

  /**
   * Verify the OTP code
   */
  async function verifyCode(code: string): Promise<boolean> {
    if (!code || code.length !== 6) {
      state.error = 'Please enter a 6-digit code';
      return false;
    }

    state.isLoading = true;
    state.error = null;
    state.verificationCode = code;

    try {
      const client = getNexusClient();
      const { data } = await client.mutate({
        mutation: VERIFY_PHONE,
        variables: {
          input: { code }
        }
      });

      if (data?.verifyAIPMPhone?.verified) {
        state.phoneVerified = true;
        clearResendCountdown();
        return true;
      }

      state.error = data?.verifyAIPMPhone?.error || 'Invalid verification code';
      return false;
    } catch (error: any) {
      state.error = error.message || 'Failed to verify code';
      return false;
    } finally {
      state.isLoading = false;
    }
  }

  /**
   * Start resend countdown timer (60 seconds)
   */
  function startResendCountdown(): void {
    clearResendCountdown();
    state.resendCountdown = 60;

    resendTimer = setInterval(() => {
      if (state.resendCountdown > 0) {
        state.resendCountdown--;
      } else {
        clearResendCountdown();
      }
    }, 1000);
  }

  /**
   * Clear the resend countdown timer
   */
  function clearResendCountdown(): void {
    if (resendTimer) {
      clearInterval(resendTimer);
      resendTimer = null;
    }
    state.resendCountdown = 0;
  }

  // ============================================================
  // Email Verification Methods
  // ============================================================

  /**
   * Validate an email address
   */
  function validateEmail(email: string): EmailValidationResult {
    if (!email || email.trim() === '') {
      return { isValid: false, error: 'Email address is required' };
    }

    // Basic email regex validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, error: 'Please enter a valid email address' };
    }

    return { isValid: true };
  }

  /**
   * Set email address with validation
   * Resets verification status when email is changed/cleared
   */
  async function setEmail(email: string): Promise<void> {
    const normalizedEmail = email.trim().toLowerCase();

    // If email is being cleared or changed, reset verification status
    if (!normalizedEmail || normalizedEmail !== state.email) {
      state.emailVerified = false;
      state.emailVerificationSent = false;
    }

    state.email = normalizedEmail;
  }

  /**
   * Send email verification OTP
   */
  async function sendEmailVerification(): Promise<boolean> {
    if (!state.email) {
      state.error = 'Please enter an email address';
      return false;
    }

    const validation = validateEmail(state.email);
    if (!validation.isValid) {
      state.error = validation.error || 'Invalid email';
      return false;
    }

    state.isLoading = true;
    state.error = null;

    try {
      console.log('[useAIPMOnboarding] sendEmailVerification - email:', state.email);
      const client = getNexusClient();
      console.log('[useAIPMOnboarding] Got nexus client, calling mutation...');
      const { data } = await client.mutate({
        mutation: SEND_EMAIL_VERIFICATION,
        variables: {
          input: { email: state.email }
        }
      });
      console.log('[useAIPMOnboarding] sendEmailVerification response:', data);

      if (data?.sendAIPMEmailVerification?.sent) {
        state.emailVerificationSent = true;
        startEmailResendCountdown();
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('[useAIPMOnboarding] sendEmailVerification error:', error);
      console.error('[useAIPMOnboarding] Error details:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError
      });
      state.error = error.message || 'Failed to send verification code';
      return false;
    } finally {
      state.isLoading = false;
    }
  }

  /**
   * Verify the email OTP code
   */
  async function verifyEmailCode(code: string): Promise<boolean> {
    if (!code || code.length !== 6) {
      state.error = 'Please enter a 6-digit code';
      return false;
    }

    state.isLoading = true;
    state.error = null;
    state.emailVerificationCode = code;

    try {
      const client = getNexusClient();
      const { data } = await client.mutate({
        mutation: VERIFY_EMAIL,
        variables: {
          input: { code }
        }
      });

      if (data?.verifyAIPMEmail?.verified) {
        state.emailVerified = true;
        clearEmailResendCountdown();
        return true;
      }

      state.error = data?.verifyAIPMEmail?.error || 'Invalid verification code';
      return false;
    } catch (error: any) {
      state.error = error.message || 'Failed to verify code';
      return false;
    } finally {
      state.isLoading = false;
    }
  }

  /**
   * Start email resend countdown timer (60 seconds)
   */
  function startEmailResendCountdown(): void {
    clearEmailResendCountdown();
    state.emailResendCountdown = 60;

    emailResendTimer = setInterval(() => {
      if (state.emailResendCountdown > 0) {
        state.emailResendCountdown--;
      } else {
        clearEmailResendCountdown();
      }
    }, 1000);
  }

  /**
   * Clear the email resend countdown timer
   */
  function clearEmailResendCountdown(): void {
    if (emailResendTimer) {
      clearInterval(emailResendTimer);
      emailResendTimer = null;
    }
    state.emailResendCountdown = 0;
  }

  // ============================================================
  // Channel Preference Methods
  // ============================================================

  /**
   * Set preferred communication channel
   */
  function setPreferredChannel(channel: PreferredChannel): void {
    state.preferredChannel = channel;
  }

  // ============================================================
  // Coaching Methods (Stories F-S6/7)
  // ============================================================

  /**
   * Confirm a coaching booking (connected tier).
   *
   * Called when the booking surface emits a successful booking. Marks the
   * participant as booked and captures the next call time when the payload
   * provides one.
   */
  function confirmCoachingBooking(payload?: any): void {
    state.coachingBooked = true;
    // Calendly's event_scheduled payload nests the invitee/event; be defensive
    // about shape and fall back to common fields.
    const nextAt =
      payload?.event?.start_time ||
      payload?.startTime ||
      payload?.scheduledStart ||
      payload?.start_time ||
      null;
    if (nextAt) {
      state.nextCoachingAt = String(nextAt);
    }
    // FIX A: durably persist the booking so the required coaching counts as
    // `satisfied` even without a CoachingSession (the link / self-attest tier where
    // the Calendly webhook isn't wired) — the module then stops re-popping. Best-
    // effort + fire-and-forget: never blocks the onboarding flow.
    persistCoachingAcknowledged().catch(() => {
      /* best-effort: a failed persist must never break onboarding */
    });
  }

  /**
   * Set whether the participant has self-attested to booking their call
   * (link tier — we can't observe the booking directly).
   */
  function setCoachingSelfAttested(value: boolean): void {
    state.coachingSelfAttested = value;
    // FIX A: only the affirmative "I've booked my call" attestation persists the
    // durable flag (un-checking never un-satisfies — the backend flag is sticky).
    if (value) {
      persistCoachingAcknowledged().catch(() => {
        /* best-effort: a failed persist must never break onboarding */
      });
    }
  }

  /**
   * Durably stamp Student.aipm.coachingAcknowledgedAt for the current program so the
   * required coaching stops re-prompting (Program-Modules coaching fix A). No-op when
   * coaching is off or there's no program context; silent-degrades on any error.
   */
  async function persistCoachingAcknowledged(): Promise<void> {
    if (!isFeatureEnabled('ENABLE_COACHING') || !state.programId) return;
    try {
      await getCoaching()?.acknowledgeCoachingBooking(state.programId);
    } catch (err: any) {
      console.warn('[useAIPMOnboarding] persistCoachingAcknowledged failed:', err?.message);
    }
  }

  /**
   * Build the coaching entry for the confirmation summary. Returns null when
   * the coaching phase wasn't shown (so the confirmation hides the item).
   */
  function buildCoachingSummary(): CoachingSummary | null {
    if (!shouldShowCoaching.value) return null;
    return {
      booked: state.coachingBooked || state.coachingSelfAttested,
      nextAt: state.nextCoachingAt,
      coach: state.selectedPersona?.name || 'your coach',
      tier: tierFor(state.personaScheduling)
    };
  }

  // ============================================================
  // Goal Methods
  // ============================================================

  /**
   * Set the first goal
   */
  function setFirstGoal(goal: string): void {
    // Limit to 280 characters
    state.firstGoal = goal.slice(0, 280);
  }

  /**
   * Pre-fill user data from existing profile
   *
   * This allows the onboarding flow to respect existing user data
   * such as phone number, email, and verification status.
   */
  function prefillUserData(userData: {
    phoneNumber?: string;
    phoneVerified?: boolean;
    email?: string;
    emailVerified?: boolean;
  }): void {
    console.log('[useAIPMOnboarding] prefillUserData called with:', userData);

    // Pre-fill phone number if available
    if (userData.phoneNumber) {
      const validation = validatePhoneNumber(userData.phoneNumber);
      console.log('[useAIPMOnboarding] Phone validation result:', validation);
      if (validation.isValid && validation.formatted) {
        state.phoneNumber = validation.formatted;
      } else {
        state.phoneNumber = userData.phoneNumber;
      }
    }

    // Set phone verification status
    if (userData.phoneVerified) {
      state.phoneVerified = true;
    }

    // Pre-fill email if available
    if (userData.email) {
      state.email = userData.email.trim().toLowerCase();
    }

    // Set email verification status
    if (userData.emailVerified) {
      state.emailVerified = true;
    }

    console.log('[useAIPMOnboarding] Pre-filled user data - final state:', {
      phoneNumber: state.phoneNumber,
      phoneVerified: state.phoneVerified,
      email: state.email,
      emailVerified: state.emailVerified
    });
  }

  // ============================================================
  // Coordinator Settings
  // ============================================================

  /**
   * Set coordinator settings
   */
  /**
   * Apply the program's AutoPilot channel configuration to onboarding state.
   *
   * FIRST PRINCIPLE: the set of contact channels offered to a participant is a
   * property of the program (autoPilot.channels), so it must be applied at EVERY
   * entry point that opens the dialog — both the checkout path (startOnboarding)
   * and the retroactive trigger in App.vue. Previously only startOnboarding set it,
   * so the retroactive path left `availableChannels` at its default ['sms','email'],
   * leaking Email into SMS-only programs.
   */
  function applyAutoPilotChannels(autoPilot?: any): void {
    if (!autoPilot?.channels) return;
    const channels: Array<'sms' | 'email'> = [];
    if (autoPilot.channels.sms?.enabled) channels.push('sms');
    if (autoPilot.channels.email?.enabled) channels.push('email');
    if (channels.length > 0) {
      state.availableChannels = channels;
      // Auto-select when only a single channel is offered.
      if (channels.length === 1) {
        [state.preferredChannel] = channels;
      }
    }
  }

  function setCoordinatorSettings(settings: Partial<CoordinatorSettings>): void {
    state.coordinatorSettings = {
      ...state.coordinatorSettings,
      ...settings
    };
  }

  // ============================================================
  // Completion Methods
  // ============================================================

  /**
   * Complete the onboarding flow
   * Uses fetch instead of Apollo client to avoid context issues
   */
  async function complete(): Promise<boolean> {
    state.isLoading = true;
    state.error = null;

    try {
      if (state.programId) {
        // Use fetch instead of Apollo client to avoid context issues
        const response = await fetch(
          `${import.meta.env.VITE_NEXUS_API || 'http://localhost:3007'}/graphql`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
            },
            body: JSON.stringify({
              query: `
                mutation CompleteAIPMOnboarding($input: CompleteOnboardingInput!) {
                  completeAIPMOnboarding(input: $input) {
                    completed
                    messageId
                  }
                }
              `,
              variables: {
                input: {
                  programId: state.programId,
                  preferredChannel: state.preferredChannel,
                  firstGoal: state.firstGoal || undefined,
                  // Optimistic client signal: participant booked (connected-tier
                  // Calendly event_scheduled) or self-attested (link tier). Lets the
                  // server-side required-coaching gate pass before the webhook lands.
                  coachingBooked: state.coachingBooked || state.coachingSelfAttested || undefined
                }
              }
            })
          }
        );

        const result = await response.json();
        console.log('[useAIPMOnboarding] Complete onboarding result:', result);

        if (result.errors) {
          throw new Error(result.errors[0]?.message || 'Failed to complete onboarding');
        }
      }

      // Update completion state
      state.isComplete = true;
      state.completedAt = new Date();
      state.currentPhase = 'confirmation';

      // Prepare message preview
      state.messagePreview = {
        personaName: state.selectedPersona?.name || 'Your Success Manager',
        goal: state.firstGoal || undefined,
        channel: state.preferredChannel
      };

      // Prepare confirmation summary
      state.confirmationSummary = {
        persona: state.selectedPersona?.name || 'Your Success Manager',
        channel: state.preferredChannel,
        goal: state.firstGoal || undefined,
        coaching: buildCoachingSummary()
      };

      return true;
    } catch (error: any) {
      console.error('[useAIPMOnboarding] Complete error:', error);
      state.error = error.message || 'Failed to complete onboarding';
      return false;
    } finally {
      state.isLoading = false;
    }
  }

  // ============================================================
  // Dismiss Methods
  // ============================================================

  /**
   * Dismiss the onboarding dialog
   *
   * Respects coordinator settings for skippable onboarding.
   * Uses fetch instead of Apollo client to avoid context issues.
   */
  async function dismiss(): Promise<DismissResult> {
    // Check if dismissal is allowed by coordinator settings
    if (!state.coordinatorSettings.skippable) {
      return {
        success: false,
        reason: 'onboarding_required'
      };
    }

    state.isLoading = true;

    try {
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours

      if (state.programId) {
        // Use fetch instead of Apollo client to avoid context issues
        const response = await fetch(
          `${import.meta.env.VITE_NEXUS_API || 'http://localhost:3007'}/graphql`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
            },
            body: JSON.stringify({
              query: `
                mutation DismissAIPMOnboarding($input: DismissOnboardingInput!) {
                  dismissAIPMOnboarding(input: $input) {
                    dismissed
                    expiresAt
                  }
                }
              `,
              variables: {
                input: { programId: state.programId }
              }
            })
          }
        );

        const result = await response.json();
        console.log('[useAIPMOnboarding] Dismiss result:', result);
      }

      state.lastDismissedAt = new Date();
      state.isOpen = false;

      // Record permanent dismissal so it never auto-pops again
      if (state.programId) {
        setPermanentlyDismissed(state.programId);
      }

      return {
        success: true,
        reason: 'dismissed',
        expiresAt
      };
    } catch (error: any) {
      console.error('[useAIPMOnboarding] Dismiss error:', error);
      // Close dialog even if API fails
      state.lastDismissedAt = new Date();
      state.isOpen = false;

      // Record permanent dismissal even on API failure
      if (state.programId) {
        setPermanentlyDismissed(state.programId);
      }

      return {
        success: true,
        reason: 'dismissed',
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000)
      };
    } finally {
      state.isLoading = false;
    }
  }

  /**
   * Close the dialog (alias for dismiss when allowed, or hide UI)
   */
  function close(): void {
    console.log(
      '[useAIPMOnboarding] close() called, skippable:',
      state.coordinatorSettings.skippable
    );
    if (state.coordinatorSettings.skippable) {
      console.log('[useAIPMOnboarding] Calling dismiss()');
      dismiss();
    } else {
      // If not skippable, just close the UI without recording as dismissal
      console.log('[useAIPMOnboarding] Setting state.isOpen = false directly');
      state.isOpen = false;
    }
    console.log('[useAIPMOnboarding] After close(), state.isOpen:', state.isOpen);
  }

  // ============================================================
  // Reset Methods
  // ============================================================

  /**
   * Reset the onboarding state
   */
  function reset(): void {
    clearResendCountdown();
    clearEmailResendCountdown();

    state.isOpen = false;
    state.isLoading = false;
    state.error = null;
    state.currentPhase = 'welcome';
    state.programId = null;
    state.entryPoint = null;
    state.sessionId = null;
    state.availablePersonas = [];
    state.selectedPersona = null;
    state.phoneNumber = '';
    state.phoneVerified = false;
    state.verificationCode = '';
    state.verificationSent = false;
    state.verificationAttempts = 0;
    state.resendCountdown = 0;
    state.email = '';
    state.emailVerified = false;
    state.emailVerificationSent = false;
    state.emailVerificationCode = '';
    state.emailResendCountdown = 0;
    state.preferredChannel = 'sms';
    state.coachingPolicy = null;
    state.personaScheduling = null;
    state.coachingBooked = false;
    state.coachingSelfAttested = false;
    state.nextCoachingAt = null;
    state.coachingAlreadyBooked = false;
    state.coachingCoverage = null;
    state.availableChannels = ['sms', 'email'];
    state.firstGoal = '';
    state.isComplete = false;
    state.completedAt = null;
    state.messagePreview = null;
    state.confirmationSummary = null;
    state.skipPhoneVerification = false;
    state.skipEmailVerification = false;
  }

  // ============================================================
  // Return Composable API
  // ============================================================

  return {
    // State (reactive)
    state,

    // Computed
    phaseOrder: readonly(phaseOrder),
    currentPhaseIndex: readonly(currentPhaseIndex),
    progress: readonly(progress),
    canGoBack: readonly(canGoBack),
    canGoNext: readonly(canGoNext),
    isDialogOpen: readonly(isDialogOpen),
    shouldShowCoaching: readonly(shouldShowCoaching),
    shouldShowTeam: readonly(shouldShowTeam),

    // Navigation
    nextPhase,
    prevPhase,
    goToPhase,

    // Onboarding flow
    startOnboarding,
    hydrateConfigFromAutoPilot,
    loadPersonas,
    selectPersona,
    fetchVerificationStatus,

    // Team Onboarding — re-check team membership after an in-onboarding join so the mid-
    // program gate (teamOnTeam) is pre-satisfied and the phase reflects the new state.
    refreshTeamEnabled,

    // Phone verification
    validatePhoneNumber,
    formatPhoneNumber,
    setPhoneNumber,
    sendVerification,
    verifyCode,

    // Email verification
    validateEmail,
    setEmail,
    sendEmailVerification,
    verifyEmailCode,

    // Channel preference
    setPreferredChannel,

    // Coaching (Stories F-S6/7)
    confirmCoachingBooking,
    setCoachingSelfAttested,

    // Goal
    setFirstGoal,

    // User data pre-fill
    prefillUserData,

    // Coordinator settings
    setCoordinatorSettings,
    applyAutoPilotChannels,

    // Completion
    complete,

    // Dismiss
    dismiss,
    close,

    // Permanent dismissal
    isPermanentlyDismissed,
    setPermanentlyDismissed,
    clearPermanentDismissal,

    // Centralized gate
    canStartOnboarding,

    // Reset
    reset
  };
  /* eslint-enable no-use-before-define */
}

// ============================================================
// Public API
// ============================================================

/**
 * useAIPMOnboarding - Singleton composable for AIPM onboarding
 *
 * This ensures that all components share the same onboarding instance,
 * which is critical for state synchronization between the dialog component
 * and components that trigger onboarding actions.
 *
 * @returns The complete onboarding API with all methods and state
 */
export function useAIPMOnboarding() {
  // Create instance only once
  if (!onboardingInstance) {
    onboardingInstance = createOnboardingComposable();
  }

  // Cache the Apollo client if we're in a Vue component context
  // This ensures async functions can access it later
  if (!cachedNexusClient) {
    try {
      cachedNexusClient = useNexusClient();
    } catch (e) {
      // Not in a component context yet, will be cached later
      console.debug('[useAIPMOnboarding] Apollo client will be cached on first component use');
    }
  }

  // Cache the coaching composable in the same setup context (Stories F-S6/7)
  if (!cachedCoaching) {
    try {
      cachedCoaching = useCoaching();
    } catch (e) {
      // Not in a component context yet, will be cached later
      console.debug(
        '[useAIPMOnboarding] Coaching composable will be cached on first component use'
      );
    }
  }

  // Cache the coaching MODULE composable in the same setup context (booked-state skip).
  if (!cachedCoachingModule) {
    try {
      cachedCoachingModule = useCoachingModule();
    } catch (e) {
      // Not in a component context yet, will be cached later
      console.debug(
        '[useAIPMOnboarding] Coaching module composable will be cached on first component use'
      );
    }
  }

  return onboardingInstance;
}

/**
 * Reset the singleton instance
 *
 * Only use this in tests or when explicitly needing to reset state
 */
export function resetOnboardingInstance(): void {
  if (onboardingInstance) {
    onboardingInstance.reset();
  }
  onboardingInstance = null;
}

// Default export
export default useAIPMOnboarding;
